import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useApi } from './ApiContext';
import { useAuth } from './AuthContext';

const ChatContext = createContext(null);

// Notification sound (short beep)
const NOTIFICATION_SOUND_DATA = 'data:audio/wav;base64,UklGRl9vT19teleXBNVFIgAAAAQ0RBdGESABIAAgAAAEAAAA==';

let notificationAudio;
try {
  notificationAudio = new Audio(NOTIFICATION_SOUND_DATA);
  notificationAudio.volume = 0.3;
} catch { notificationAudio = null; }

export const ChatProvider = ({ children }) => {
  const { im } = useApi();
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messagesCache, setMessagesCache] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [openBubbles, setOpenBubbles] = useState([]);
  const [openWindows, setOpenWindows] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState({});

  const typingTimers = useRef({});
  const myId = user?.agent || user?.username || null;

  // Load conversations
  const loadConversations = useCallback(async (type) => {
    try {
      const res = await im.getConversations(type ? { type } : undefined);
      if (res.success) {
        setConversations(res.data);
        const unread = res.data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setTotalUnread(unread);
      }
    } catch (e) {
      console.error('[Chat] loadConversations error:', e);
    }
  }, [im]);

  // Load messages for a conversation
  const loadMessages = useCallback(async (convId, before) => {
    try {
      const params = { limit: 50 };
      if (before) params.before = before;
      const res = await im.getMessages(convId, params);
      if (res.success) {
        setMessagesCache(prev => {
          const existing = before ? (prev[convId] || []) : [];
          const merged = before ? [...res.data, ...existing] : res.data;
          return { ...prev, [convId]: merged };
        });
        setHasMoreMessages(prev => ({ ...prev, [convId]: res.hasMore }));
      }
      return res;
    } catch (e) {
      console.error('[Chat] loadMessages error:', e);
    }
  }, [im]);

  // Send message
  const sendMessage = useCallback(async (convId, content, files, replyToId) => {
    try {
      const formData = new FormData();
      if (content) formData.append('content', content);
      if (replyToId) formData.append('reply_to_id', replyToId);
      if (files) {
        for (const file of files) {
          formData.append('files', file);
        }
      }
      const res = await im.sendMessage(convId, formData);
      if (res.success) {
        // Add to cache
        setMessagesCache(prev => ({
          ...prev,
          [convId]: [...(prev[convId] || []), res.data]
        }));
        // Update conversation list order
        setConversations(prev => {
          const updated = prev.map(c =>
            c.id === convId
              ? { ...c, last_message: res.data.content, last_message_at: res.data.created_at, last_message_sender: myId, last_message_type: res.data.message_type }
              : c
          );
          return updated.sort((a, b) => {
            const ta = a.last_message_at ? new Date(a.last_message_at) : new Date(0);
            const tb = b.last_message_at ? new Date(b.last_message_at) : new Date(0);
            return tb - ta;
          });
        });
      }
      return res;
    } catch (e) {
      console.error('[Chat] sendMessage error:', e);
      throw e;
    }
  }, [im, myId]);

  // Mark as read
  const markAsRead = useCallback(async (convId) => {
    try {
      await im.markRead(convId);
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
      setTotalUnread(prev => {
        const conv = conversations.find(c => c.id === convId);
        return Math.max(0, prev - (conv?.unread_count || 0));
      });
    } catch (e) {
      console.error('[Chat] markAsRead error:', e);
    }
  }, [im, conversations]);

  // Bubble/window management
  const openBubbleWindow = useCallback((convId) => {
    setOpenWindows(prev => {
      if (prev.includes(convId)) return prev;
      const next = [...prev, convId].slice(-3); // max 3 windows
      return next;
    });
    setOpenBubbles(prev => prev.filter(id => id !== convId));
  }, []);

  const closeBubbleWindow = useCallback((convId) => {
    setOpenWindows(prev => prev.filter(id => id !== convId));
    setOpenBubbles(prev => prev.filter(id => id !== convId));
  }, []);

  const minimizeWindow = useCallback((convId) => {
    setOpenWindows(prev => prev.filter(id => id !== convId));
    setOpenBubbles(prev => prev.includes(convId) ? prev : [...prev, convId]);
  }, []);

  // Emit typing indicator
  const sendTyping = useCallback((conversationId, isTyping) => {
    const socket = window.__ctiSfuSocket;
    if (socket) {
      socket.emit('chat.typing', { conversationId, isTyping });
    }
  }, []);

  // Load online agents on mount
  useEffect(() => {
    im.getOnlineAgents().then(res => {
      if (res.success && Array.isArray(res.data)) {
        setOnlineUsers(new Set(res.data));
      }
    }).catch(() => {});
  }, [im]);

  // WebSocket event listeners
  useEffect(() => {
    const socket = window.__ctiSfuSocket;
    if (!socket) return;

    const handleMessage = (data) => {
      const { conversationId, message } = data;

      // Add to cache if conversation is loaded
      setMessagesCache(prev => {
        if (!prev[conversationId]) return prev;
        // Avoid duplicate
        if (prev[conversationId].some(m => m.id === message.id)) return prev;
        return { ...prev, [conversationId]: [...prev[conversationId], message] };
      });

      // Update conversation in list
      setConversations(prev => {
        let found = false;
        const updated = prev.map(c => {
          if (c.id === conversationId) {
            found = true;
            const isActive = c.id === activeConversationId;
            return {
              ...c,
              last_message: message.content,
              last_message_at: message.created_at,
              last_message_sender: message.sender_id,
              last_message_type: message.message_type,
              unread_count: isActive ? c.unread_count : (c.unread_count || 0) + 1
            };
          }
          return c;
        });
        if (!found) {
          // Conversation not in list, reload
          loadConversations();
          return prev;
        }
        // Update total unread
        const newTotal = updated.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setTotalUnread(newTotal);
        return updated.sort((a, b) => {
          const ta = a.last_message_at ? new Date(a.last_message_at) : new Date(0);
          const tb = b.last_message_at ? new Date(b.last_message_at) : new Date(0);
          return tb - ta;
        });
      });

      // Show bubble if not active
      if (conversationId !== activeConversationId) {
        setOpenBubbles(prev => {
          if (prev.includes(conversationId)) return prev;
          if (openWindows.includes(conversationId)) return prev;
          return [...prev, conversationId].slice(-4);
        });

        // Play notification sound
        const soundEnabled = localStorage.getItem('chat_sound_enabled') !== 'false';
        if (soundEnabled && notificationAudio) {
          notificationAudio.play().catch(() => {});
        }
      }
    };

    const handleTyping = (data) => {
      const { conversationId, agentId, isTyping } = data;
      const key = `${conversationId}_${agentId}`;

      if (isTyping) {
        setTypingUsers(prev => {
          const convTyping = new Set(prev[conversationId] || []);
          convTyping.add(agentId);
          return { ...prev, [conversationId]: convTyping };
        });
        // Auto-clear after 3 seconds
        if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
        typingTimers.current[key] = setTimeout(() => {
          setTypingUsers(prev => {
            const convTyping = new Set(prev[conversationId] || []);
            convTyping.delete(agentId);
            return { ...prev, [conversationId]: convTyping };
          });
        }, 3000);
      } else {
        if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
        setTypingUsers(prev => {
          const convTyping = new Set(prev[conversationId] || []);
          convTyping.delete(agentId);
          return { ...prev, [conversationId]: convTyping };
        });
      }
    };

    const handleOnline = (data) => {
      const { agentId, online } = data;
      setOnlineUsers(prev => {
        const next = new Set(prev);
        if (online) next.add(agentId);
        else next.delete(agentId);
        return next;
      });
    };

    const handleRead = (data) => {
      // Could update read receipts UI if needed
    };

    socket.on('chat.message', handleMessage);
    socket.on('chat.typing', handleTyping);
    socket.on('chat.online', handleOnline);
    socket.on('chat.read', handleRead);

    return () => {
      socket.off('chat.message', handleMessage);
      socket.off('chat.typing', handleTyping);
      socket.off('chat.online', handleOnline);
      socket.off('chat.read', handleRead);
    };
  }, [activeConversationId, openWindows, loadConversations]);

  // Initial load
  useEffect(() => {
    if (myId) {
      loadConversations();
      // Fetch unread count
      im.getUnreadCount().then(res => {
        if (res.success) setTotalUnread(res.data.count);
      }).catch(() => {});
    }
  }, [myId]);

  const value = {
    conversations,
    activeConversationId,
    setActiveConversationId,
    messagesCache,
    totalUnread,
    onlineUsers,
    typingUsers,
    openBubbles,
    setOpenBubbles,
    openWindows,
    setOpenWindows,
    hasMoreMessages,
    loadConversations,
    loadMessages,
    sendMessage,
    markAsRead,
    openBubbleWindow,
    closeBubbleWindow,
    minimizeWindow,
    sendTyping,
    myId
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
};
